import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ArrowDown, HelpCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FunnelStage {
  name: string
  count: number
  color: string
}

interface MiniConversionFunnelProps {
  data: FunnelStage[]
  loading?: boolean
}

const STAGE_TOOLTIPS: Record<string, string> = {
  'Widget Loaded': 'Visitors who landed on a page with your chat widget.',
  'Chat Opened': 'Visitors who clicked the chat bubble.',
  'Message Sent': 'Visitors who typed and sent a message.',
  'Conversation Engaged': 'Chats with 2+ messages exchanged.',
  'Conversation Completed': 'Chats that reached a natural end.',
  'Booking Made': 'Visitors who booked a consultation.',
}

export default function MiniConversionFunnel({ data, loading = false }: MiniConversionFunnelProps) {
  const maxCount = Math.max(...data.map(d => d.count), 1)

  const getStageColor = (index: number) => {
    const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500']
    return colors[index % colors.length]
  }

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Conversion Funnel</CardTitle>
          <CardDescription>Today's visitor journey</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-8 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (data.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Conversion Funnel</CardTitle>
          <CardDescription>Today's visitor journey</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground text-sm">
            No funnel data available
          </div>
        </CardContent>
      </Card>
    )
  }

  const firstCount = data[0]?.count || 0
  const lastCount = data[data.length - 1]?.count || 0
  const endToEnd = firstCount > 0 ? ((lastCount / firstCount) * 100).toFixed(1) : '0'

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Conversion Funnel</CardTitle>
            <CardDescription>Today's visitor journey</CardDescription>
          </div>
          {firstCount > 0 && (
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">{endToEnd}%</p>
              <p className="text-xs text-muted-foreground">End-to-end</p>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-1.5">
          {data.map((stage, index) => {
            const previousCount = index > 0 ? data[index - 1].count : null
            const widthPercentage = stage.count > 0 && maxCount > 0 ? Math.max(8, (stage.count / maxCount) * 100) : 0

            let stepRate: string | null = null
            if (previousCount !== null && previousCount > 0 && stage.count <= previousCount) {
              stepRate = ((stage.count / previousCount) * 100).toFixed(0)
            }

            return (
              <div key={stage.name}>
                {/* Stage Row */}
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-medium">{stage.name}</span>
                        {STAGE_TOOLTIPS[stage.name] && (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-3 w-3 text-muted-foreground/40 hover:text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs text-xs">
                                {STAGE_TOOLTIPS[stage.name]}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-sm font-bold">{stage.count.toLocaleString()}</span>
                        {stepRate !== null && (
                          <span className={cn(
                            'text-xs font-medium',
                            parseInt(stepRate) >= 50 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                          )}>
                            {stepRate}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="relative w-full h-5 bg-muted rounded overflow-hidden">
                      <div
                        className={cn('h-full transition-colors duration-500', getStageColor(index))}
                        style={{ width: `${widthPercentage}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                {index < data.length - 1 && (
                  <div className="flex justify-center py-0.5">
                    <ArrowDown className="h-3 w-3 text-muted-foreground/30" />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
